import io from 'socket.io-client';
import crypto from 'crypto-browserify';
import { publicKey } from './public-key';

const isMobile = (function() {
	let check = false;
	(function(a) {
		if (
			//eslint-disable-next-line no-useless-escape
			/(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
				a
			) ||
			//eslint-disable-next-line no-useless-escape
			/1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
				a.substr(0, 4)
			)
		) { check = true; }
	})(navigator.userAgent || navigator.vendor || (window as any).opera);
	return check;
})();

declare global {
	interface Document {
		msHidden: any;
		webkitHidden: any;
	}
}

let hidden: 'hidden' | 'msHidden' | 'webkitHidden';
let visibilityChange: string;
if (typeof document.hidden !== "undefined") { // Opera 12.10 and Firefox 18 and later support 
	hidden = "hidden";
	visibilityChange = "visibilitychange";
} else if (typeof document.msHidden !== "undefined") {
	hidden = "msHidden";
	visibilityChange = "msvisibilitychange";
} else if (typeof document.webkitHidden !== "undefined") {
	hidden = "webkitHidden";
	visibilityChange = "webkitvisibilitychange";
}

interface Listener {
	// eslint-disable-next-line @typescript-eslint/ban-types
	[key: string]: Function[];
}

export interface SocketMessage {
	type: string;
	data: any;
	message?: any;
	signature?: string;
}

export default class Socket {
	connection: SocketIOClient.Socket;
	shouldRestart = false;
	listeners: Listener = {};
	hasHidden = false;
	channel: string;
	ready = false;
	isReconnection = false;
	waitingMessages: SocketMessage[] = [];
	visibilityListenerAdded = false;
	onCurrentlyWatching?: (viewers: string) => void;

	constructor(channel: string) {
		this.channel = channel;
		this.connection = io.connect(
			process.env.REACT_APP_SOCKET_HOST as string,
			{
				transports: ['websocket'],
			}
		);
		this.connection.on('connect', this.handleConnection);
		this.connection.on('error', this.handleError);
		this.connection.on('disconnect', this.handleDisconnect);
		this.connection.on('message', this.handleMessage);

		if (!this.visibilityListenerAdded && isMobile) {
			document.addEventListener(visibilityChange, this.handleAppState, false);
			this.visibilityListenerAdded = true;
		}
	}

	getCurrentlyWatching = (callback: (viewers: { viewers: string }) => void): void => {
		this.connection.once('currently-watching', callback);
		this.connection.emit('get-currently-watching', this.channel);
	}

	handleConnection = (): void => {
		if (!this.ready) {
			this.ready = true;
			this.connection.emit('join-channel', this.channel);

			//if we have messages queued up before the connection is ready, fire them all now that we have a connection
			if (this.waitingMessages.length > 0) {
				for (const message of this.waitingMessages) {
					this.connection.emit('message', message, this.channel);
				}

				this.waitingMessages = [];
			}
		}
	};

	handleError = (e: Error): void => {
		console.error(e);

		if (this.connection) {
			this.connection.disconnect();
		}

		setTimeout(() => {
			this.isReconnection = true;

			this.connection = io.connect(
				process.env.REACT_APP_SOCKET_HOST as string,
				{ transports: ['websocket'] }
			);
		});
	};

	handleDisconnect = (): void => {
		this.ready = false;
	};

	handleMessage = (message: SocketMessage): void => {
		if ('message' in message) { message.data = message.message; }

		if ('signature' in message) {
			const verifier = crypto.createVerify('RSA-SHA256');

			verifier.write(JSON.stringify(message.message));

			verifier.end();

			const ver = verifier.verify(publicKey, message.signature, 'base64');

			if (ver) {
				this.listeners[message.message.type]?.forEach((func) => func(message.message));
			} else {
				console.log({ message }, "Sneaky sneaky...");
			}
		} else {
			this.listeners[message.type]?.forEach((func) => func(message?.data ?? message));
		}
	};

	handleAppState = (): void => {
		//this will fire once as soon as the page loads, and we only want to
		//handle a reconnection ONLY when the page has become active after having already been hidden
		if (document[hidden]) {
			this.hasHidden = true;
		} else {
			if (this.hasHidden) {
				//end the connection, wait a half second, then reconnect fresh
				if (this.connection) { this.connection.disconnect(); }

				setTimeout(() => {
					this.isReconnection = true;
					this.connection = io.connect(
						process.env.REACT_APP_SOCKET_HOST as string,
						{ transports: ['websocket'] }
					);
					this.connection.on('connect', this.handleConnection);
					this.connection.on('error', this.handleError);
					this.connection.on('disconnect', this.handleDisconnect);
					this.connection.on('message', this.handleMessage);
				}, 500);
			}
		}
	};

	// eslint-disable-next-line @typescript-eslint/ban-types
	addListener = (type: string, func: Function): void => {
		if (this.listeners[type]) {
			this.listeners[type] = [...this.listeners[type], func];
		} else {
			this.listeners[type] = [func];
		}
	};

	// eslint-disable-next-line @typescript-eslint/ban-types
	removeListener = (type: string, func: Function): void => {
		this.listeners[type] =
			this.listeners[type]?.filter((_func) => _func !== func) ?? [];
	};

	sendMessage = (message: SocketMessage): void => {
		if (!this.ready) {
			this.waitingMessages = [...this.waitingMessages, message];
		} else {
			this.connection.emit('message', message, this.channel);
		}
	};

	removeAllListeners = (): void => {
		this.listeners = {};
	};

	disconnect = (): void => {
		this.connection.close();
	}
}

interface SocketConnection {
	[key: string]: Socket;
}

const connections: SocketConnection = {};

export function useSocket(channel: string): Socket {
	if (!connections[channel]) {
		connections[channel] = new Socket(channel);
	}
	return connections[channel];
}
